import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import { CircularProgress, Card } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { signIn, verifyGoogleRecaptcha } from 'store/auth'
import { getConsoleHome } from '../../routes'
import { useHistory } from 'react-router-dom'
import { useFormik } from 'formik'
import { Box } from '@mui/material'
import { userLoginSchema } from 'schemas/admins'
import Recaptcha from 'components/Recaptcha'
import useRecaptcha from 'hooks/useCustomRecaptcha'
import AppLogo from '../../assets/primary_logo.png'
import { colors } from 'utils/colors'
import PasswordField from 'components/PasswordField'
import TextField from '@material-ui/core/TextField'

const Login = () => {
  const { isRecaptchaValid, recaptchaCode, handleChangeRecaptcha, handleExpired } = useRecaptcha()
  const [remountRecaptcha, setRemountRecaptcha] = useState(false)

  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const [inProgress, setProgress] = useState(false)
  const [loginError, setLoginError] = useState('')
  const { user, loginInProgress } = useSelector((state) => state.auth)

  useEffect(() => {
    if (user?.id && !loginInProgress) {
      history.push(getConsoleHome(user))
    }
  }, [user, loginInProgress])

  const onSubmit = async (values) => {
    try {
      setProgress(true)
      setLoginError('')
      const isRecaptchaVerified = await dispatch(verifyGoogleRecaptcha(recaptchaCode))
      if (isRecaptchaVerified) {
        const result = await dispatch(signIn(values.email, values.password))
        if (!result || result.error) {
          setRemountRecaptcha(true)
          setLoginError(result?.error || 'Incorrect Email or Password, Please try again.')
          return
        }
        if (result.needsPicker) {
          history.push('/select-conference', {
            partialUser: result.partialUser,
            assignments: result.assignments
          })
          return
        }
        history.push(getConsoleHome(result.user))
      } else {
        setLoginError('reCAPTCHA verification failed. Try again.')
      }
    } catch (e) {
      console.error(e)
    } finally {
      setProgress(false)
    }
  }

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema: userLoginSchema,
    onSubmit: onSubmit
  })

  return (
    <div className={classes.root}>
      <div className={classes.overlay}></div>
      <Card className={classes.card} elevation={5}>
        <div className={classes.logoContainer}>
          <img src={AppLogo} alt="MeetSymple Logo" className={classes.logo} />
        </div>
        <Typography className={classes.header} variant="h5">
          Conference Management
        </Typography>
        <Typography className={classes.subHeader} variant="body2">
          Sign in to access your conference dashboard
        </Typography>

        <form className={classes.form} onSubmit={formik.handleSubmit}>
          <TextField
            variant="outlined"
            label="Email"
            name="email"
            className={classes.textField}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          <PasswordField label="Password" name="password" formik={formik} />

          <Box
            textAlign="right"
            style={inProgress ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}>
            <a
              onClick={() => history.push('/forgot-password')}
              color="primary"
              className={classes.forgotPasswordLink}
              style={{ textTransform: 'none', padding: '2px 0' }}>
              Forgot Password?
            </a>
          </Box>

          <Box className={classes.recaptchaContainer}>
            <Recaptcha
              handleChange={handleChangeRecaptcha}
              onExpired={handleExpired}
              remount={remountRecaptcha}
            />
          </Box>

          {loginError && (
            <Typography className={classes.loginError} variant="body2">
              {loginError}
            </Typography>
          )}

          <Button
            className={classes.loginBtn}
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            startIcon={inProgress && <CircularProgress size={17} color="inherit" />}
            disabled={inProgress || !isRecaptchaValid}>
            {inProgress ? 'Signing in...' : 'Log In'}
          </Button>
        </form>
      </Card>
    </div>
  )
}

export default Login

const useStyles = makeStyles({
  textField: {
    marginBottom: '8px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      transition: 'all 0.3s',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#15b4db'
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e0e0e0',
      borderWidth: '1px'
    }
  },
  root: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    padding: '20px'
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
    zIndex: 1
  },
  card: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: '450px',
    borderRadius: '16px',
    padding: '35px 40px',
    boxShadow: '0 15px 25px rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(10px)',
    background: 'rgba(255, 255, 255, 0.95)',
    animation: '$fadeIn 0.8s ease-out',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
    marginLeft: '1.25rem'
  },
  logo: {
    height: '60px',
    width: 'auto',
    filter: 'drop-shadow(0 2px 5px rgba(0, 0, 0, 0.2))'
  },
  header: {
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: colors.indigo
  },
  subHeader: {
    textAlign: 'center',
    color: colors.grey,
    marginBottom: '32px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '26px',
    width: '100%'
  },
  inputIcon: {
    color: colors.indigo,
    fontSize: '20px'
  },
  forgotPasswordLink: {
    cursor: 'pointer',
    color: colors.indigo,
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  recaptchaContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '8px'
  },
  loginError: {
    color: '#d32f2f',
    textAlign: 'center',
    marginTop: '-8px'
  },
  loginBtn: {
    fontWeight: 'bold',
    textTransform: 'none',
    width: '100%',
    borderRadius: '30px',
    padding: '12px',
    background: `linear-gradient(45deg, ${colors.indigo} 30%, ${colors.blue} 90%)`,
    boxShadow: `0 3px 10px rgba(21, 180, 219, 0.3)`,
    transition: 'all 0.3s ease',
    height: 55,
    marginTop: '10px',
    '&:hover': {
      boxShadow: `0 6px 15px rgba(21, 180, 219, 0.5)`,
      transform: 'translateY(-2px)'
    }
  }
})
